import { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { PageHeader } from "@/components/Layout";
import { EmptyState } from "@/components/EmptyState";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Plus,
  Search,
  Pencil,
  Trash2,
  Loader2,
  ShieldCheck,
  UserCog,
  BriefcaseBusiness,
  Wrench,
  Mail,
  Phone,
  CheckCircle2,
  XCircle,
} from "lucide-react";
import { toast } from "sonner";

const ROLES = [
  {
    value: "admin",
    label: "Administrador",
    description: "Control total, empresa, usuarios y auditoría.",
    icon: ShieldCheck,
  },
  {
    value: "gerente",
    label: "Gerente",
    description: "Gestiona operaciones, facturación y equipo.",
    icon: UserCog,
  },
  {
    value: "oficina",
    label: "Oficina",
    description: "Clientes, avisos, agenda, presupuestos y facturas.",
    icon: BriefcaseBusiness,
  },
  {
    value: "tecnico",
    label: "Técnico",
    description: "Consulta y completa los trabajos asignados.",
    icon: Wrench,
  },
];

const EMPTY = {
  name: "",
  email: "",
  password: "",
  role: "oficina",
  phone: "",
  specialty: "",
  active: true,
};

function roleMeta(role) {
  return ROLES.find((item) => item.value === role) || ROLES[3];
}

export default function Team() {
  const { user } = useAuth();
  const canManage =
    user?.role === "admin" ||
    user?.permissions?.includes("team.manage");

  const [members, setMembers] = useState([]);
  const [query, setQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("todos");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/team");
      setMembers(data || []);
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (canManage) load();
  }, [canManage]);

  const reset = () => {
    setEditing(null);
    setForm(EMPTY);
  };

  const openCreate = () => {
    reset();
    setOpen(true);
  };

  const openEdit = (member) => {
    setEditing(member);
    setForm({
      name: member.name || "",
      email: member.email || "",
      password: "",
      role: member.role || "tecnico",
      phone: member.phone || "",
      specialty: member.specialty || "",
      active: member.active !== false,
    });
    setOpen(true);
  };

  const save = async (event) => {
    event.preventDefault();

    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Nombre y email son obligatorios.");
      return;
    }

    if (!editing && form.password.length < 8) {
      toast.error("La contraseña debe tener al menos 8 caracteres.");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...form,
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        phone: form.phone.trim(),
        specialty: form.specialty.trim(),
      };

      if (editing && !payload.password) {
        delete payload.password;
      }

      if (editing) {
        await api.put(`/team/${editing.id}`, payload);
        toast.success("Usuario actualizado.");
      } else {
        await api.post("/team", payload);
        toast.success("Usuario añadido al equipo.");
      }

      setOpen(false);
      reset();
      await load();
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (member) => {
    try {
      await api.put(`/team/${member.id}`, {
        active: member.active === false,
      });
      toast.success(
        member.active === false
          ? "Usuario activado."
          : "Usuario desactivado."
      );
      await load();
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const remove = async (member) => {
    if (
      !window.confirm(
        `¿Eliminar a ${member.name}? Perderá el acceso a Navirox.`
      )
    ) {
      return;
    }

    setDeletingId(member.id);
    try {
      await api.delete(`/team/${member.id}`);
      toast.success("Usuario eliminado.");
      await load();
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setDeletingId(null);
    }
  };

  const filtered = useMemo(() => {
    const normalized = query.trim().toLowerCase();

    return members.filter((member) => {
      const matchesSearch =
        !normalized ||
        [member.name, member.email, member.phone, member.specialty]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
          .includes(normalized);

      const matchesRole =
        roleFilter === "todos" || member.role === roleFilter;

      return matchesSearch && matchesRole;
    });
  }, [members, query, roleFilter]);

  const activeCount = members.filter(
    (member) => member.active !== false
  ).length;

  if (user && !canManage) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="p-6 sm:p-8 lg:p-12">
      <PageHeader
        title="Equipo y permisos"
        subtitle={`${members.length} usuarios · ${activeCount} activos`}
        action={
          <Dialog
            open={open}
            onOpenChange={(value) => {
              setOpen(value);
              if (!value) reset();
            }}
          >
            <DialogTrigger asChild>
              <button
                type="button"
                onClick={openCreate}
                className="flex items-center gap-2 rounded-full bg-[#0066CC] px-5 py-2.5 font-medium text-white shadow-[0_4px_12px_rgba(0,102,204,0.3)] transition-all hover:-translate-y-0.5 hover:bg-[#004499]"
              >
                <Plus className="h-4 w-4" />
                Añadir usuario
              </button>
            </DialogTrigger>

            <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto rounded-3xl border-none bg-white p-6">
              <DialogHeader>
                <DialogTitle className="font-display text-2xl">
                  {editing ? "Editar usuario" : "Nuevo usuario"}
                </DialogTitle>
                <p className="text-sm text-[#86868B]">
                  El rol determina qué módulos puede ver y modificar.
                </p>
              </DialogHeader>

              <form onSubmit={save} className="mt-5 space-y-4">
                <div>
                  <label className="mb-1.5 block text-sm font-medium">
                    Nombre
                  </label>
                  <input
                    value={form.name}
                    onChange={(event) =>
                      setForm({ ...form, name: event.target.value })
                    }
                    required
                    className="w-full rounded-xl bg-[#F5F5F7] px-4 py-3 outline-none focus:ring-2 focus:ring-[#0066CC]/20"
                  />
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <label className="mb-1.5 block text-sm font-medium">
                      Email de acceso
                    </label>
                    <input
                      type="email"
                      value={form.email}
                      onChange={(event) =>
                        setForm({ ...form, email: event.target.value })
                      }
                      required
                      className="w-full rounded-xl bg-[#F5F5F7] px-4 py-3 outline-none"
                    />
                  </div>

                  <div>
                    <label className="mb-1.5 block text-sm font-medium">
                      {editing
                        ? "Nueva contraseña"
                        : "Contraseña"}
                    </label>
                    <input
                      type="password"
                      value={form.password}
                      onChange={(event) =>
                        setForm({
                          ...form,
                          password: event.target.value,
                        })
                      }
                      required={!editing}
                      placeholder={
                        editing
                          ? "Déjala vacía para mantenerla"
                          : "Mínimo 8 caracteres"
                      }
                      className="w-full rounded-xl bg-[#F5F5F7] px-4 py-3 outline-none"
                    />
                  </div>
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium">
                    Rol
                  </label>

                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    {ROLES.map((role) => {
                      const Icon = role.icon;
                      const selected = form.role === role.value;

                      return (
                        <button
                          key={role.value}
                          type="button"
                          onClick={() =>
                            setForm({ ...form, role: role.value })
                          }
                          className={`rounded-2xl border p-4 text-left transition-all ${
                            selected
                              ? "border-[#0066CC] bg-[#EAF2FC]"
                              : "border-[#E8E8ED] bg-white hover:bg-[#F5F5F7]"
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <Icon
                              className={`h-5 w-5 ${
                                selected
                                  ? "text-[#0066CC]"
                                  : "text-[#86868B]"
                              }`}
                            />
                            <p className="font-medium">{role.label}</p>
                          </div>
                          <p className="mt-2 text-xs leading-5 text-[#86868B]">
                            {role.description}
                          </p>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <label className="mb-1.5 block text-sm font-medium">
                      Teléfono
                    </label>
                    <input
                      value={form.phone}
                      onChange={(event) =>
                        setForm({ ...form, phone: event.target.value })
                      }
                      className="w-full rounded-xl bg-[#F5F5F7] px-4 py-3 outline-none"
                    />
                  </div>

                  <div>
                    <label className="mb-1.5 block text-sm font-medium">
                      Especialidad
                    </label>
                    <input
                      value={form.specialty}
                      onChange={(event) =>
                        setForm({
                          ...form,
                          specialty: event.target.value,
                        })
                      }
                      placeholder="Especialmente para técnicos"
                      className="w-full rounded-xl bg-[#F5F5F7] px-4 py-3 outline-none"
                    />
                  </div>
                </div>

                <label className="flex items-center gap-3 rounded-2xl bg-[#F5F5F7] p-4">
                  <input
                    type="checkbox"
                    checked={form.active}
                    onChange={(event) =>
                      setForm({
                        ...form,
                        active: event.target.checked,
                      })
                    }
                    className="h-4 w-4"
                  />
                  <span>
                    <span className="block text-sm font-medium">
                      Usuario activo
                    </span>
                    <span className="text-xs text-[#86868B]">
                      Podrá iniciar sesión y utilizar sus módulos.
                    </span>
                  </span>
                </label>

                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => setOpen(false)}
                    className="flex-1 rounded-full border border-[#D2D2D7] py-3 font-medium"
                  >
                    Cancelar
                  </button>

                  <button
                    type="submit"
                    disabled={saving}
                    className="flex flex-1 items-center justify-center gap-2 rounded-full bg-[#0066CC] py-3 font-medium text-white disabled:opacity-60"
                  >
                    {saving && (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    )}
                    {saving
                      ? "Guardando..."
                      : editing
                        ? "Guardar cambios"
                        : "Añadir usuario"}
                  </button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="mantix-card mb-6 p-4">
        <div className="flex flex-col gap-3 md:flex-row">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[#86868B]" />
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Buscar por nombre, email, teléfono o especialidad..."
              className="w-full rounded-xl bg-[#F5F5F7] py-3 pl-11 pr-4 text-sm outline-none"
            />
          </div>

          <select
            value={roleFilter}
            onChange={(event) => setRoleFilter(event.target.value)}
            className="rounded-xl bg-[#F5F5F7] px-4 py-3 text-sm outline-none"
          >
            <option value="todos">Todos los roles</option>
            {ROLES.map((role) => (
              <option key={role.value} value={role.value}>
                {role.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="flex min-h-72 items-center justify-center">
          <Loader2 className="h-7 w-7 animate-spin text-[#0066CC]" />
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState
          title="No hay usuarios"
          subtitle="Añade miembros y asigna a cada persona el acceso que necesita."
        />
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {filtered.map((member) => {
            const meta = roleMeta(member.role);
            const Icon = meta.icon;
            const active = member.active !== false;

            return (
              <article
                key={member.id}
                className="mantix-card group p-5"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex min-w-0 items-center gap-3">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-[#0066CC] to-[#5856D6] font-semibold text-white">
                      {(member.name || "?").charAt(0).toUpperCase()}
                    </div>

                    <div className="min-w-0">
                      <h3 className="truncate font-medium text-[#1D1D1F]">
                        {member.name}
                      </h3>
                      <span className="mt-1 inline-flex items-center gap-1 rounded-full bg-[#F5F5F7] px-2.5 py-1 text-xs text-[#86868B]">
                        <Icon className="h-3 w-3" />
                        {meta.label}
                      </span>
                    </div>
                  </div>

                  <span
                    className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs ${
                      active
                        ? "bg-[#E9F9EE] text-[#248A3D]"
                        : "bg-[#FFEBEA] text-[#D70015]"
                    }`}
                  >
                    {active ? (
                      <CheckCircle2 className="h-3 w-3" />
                    ) : (
                      <XCircle className="h-3 w-3" />
                    )}
                    {active ? "Activo" : "Inactivo"}
                  </span>
                </div>

                <div className="mt-5 space-y-2 text-sm text-[#86868B]">
                  <p className="flex items-center gap-2">
                    <Mail className="h-4 w-4" />
                    <span className="truncate">{member.email}</span>
                  </p>

                  {member.phone && (
                    <p className="flex items-center gap-2">
                      <Phone className="h-4 w-4" />
                      {member.phone}
                    </p>
                  )}

                  {member.specialty && (
                    <p className="flex items-center gap-2">
                      <Wrench className="h-4 w-4" />
                      {member.specialty}
                    </p>
                  )}
                </div>

                <div className="mt-5 flex items-center justify-between border-t border-[#E8E8ED] pt-4">
                  <button
                    type="button"
                    onClick={() => toggleActive(member)}
                    className="text-sm font-medium text-[#0066CC]"
                  >
                    {active ? "Desactivar" : "Activar"}
                  </button>

                  <div className="flex gap-1">
                    <button
                      type="button"
                      onClick={() => openEdit(member)}
                      className="flex h-9 w-9 items-center justify-center rounded-lg text-[#86868B] hover:bg-[#EAF2FC] hover:text-[#0066CC]"
                    >
                      <Pencil className="h-4 w-4" />
                    </button>

                    <button
                      type="button"
                      onClick={() => remove(member)}
                      disabled={deletingId === member.id}
                      className="flex h-9 w-9 items-center justify-center rounded-lg text-[#B0B0B5] hover:bg-[#FFEBEA] hover:text-[#FF3B30]"
                    >
                      {deletingId === member.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Trash2 className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}